import React, { useState, useEffect } from "react";
import { TextField, Select } from "../ui";
import { journalsAPI } from "../services/api";
import { BankGlAccountDropdown } from "../shared/presentation/components";
import { GLMappingEntity } from "../shared/domain/entities/GLMappingEntity";
import { getStoredAccountingBackend } from "../config/accounting";

interface CustomerInterestIncomeRecoProps {
  transactionDate: string;
  onFromAccountChange: (accountId: string) => void;
  onDateChange: (date: string) => void;
  onReferenceNumberChange: (refNumber: string) => void;
  onPaymentModeChange: (paymentMode: string) => void;
  zohoGl?: string;
}

const CustomerInterestIncomeReco: React.FC<CustomerInterestIncomeRecoProps> = ({
  transactionDate,
  onFromAccountChange,
  onDateChange,
  onReferenceNumberChange,
  onPaymentModeChange,
  zohoGl,
}) => {
  // Zoho auto-picks the interest account; Tally needs an explicit income GL.
  const isTally = getStoredAccountingBackend() === "tally";

  const [fromAccount, setFromAccount] = useState<string>("");
  const [fromAccountName, setFromAccountName] = useState<string>("");
  const [date, setDate] = useState<string>("");
  const [referenceNumber, setReferenceNumber] = useState<string>("");
  const [paymentMode, setPaymentMode] = useState<string>("Bank Transfer");

  useEffect(() => {
    if (transactionDate) {
      const formattedDate = new Date(transactionDate)
        .toISOString()
        .split("T")[0];
      setDate(formattedDate);
      onDateChange(formattedDate);
    }
  }, [transactionDate, onDateChange]);

  useEffect(() => {
    onPaymentModeChange(paymentMode);
  }, [paymentMode, onPaymentModeChange]);

  useEffect(() => {
    if (!isTally) {
      setFromAccount("");
      setFromAccountName("");
      onFromAccountChange("");
      return;
    }

    if (!zohoGl) {
      setFromAccount("");
      setFromAccountName("");
      return;
    }

    setFromAccount(zohoGl);
    onFromAccountChange(zohoGl);

    journalsAPI
      .getGLMapByAccountId(zohoGl)
      .then((response) => {
        if (response.success && response.data) {
          setFromAccount(String(response.data.account_id));
          setFromAccountName(response.data.gl_name ?? "");
          onFromAccountChange(String(response.data.account_id));
        }
      })
      .catch((error) => {
        console.error("Failed to fetch GL account by ID:", error);
      });
  }, [isTally, zohoGl, onFromAccountChange]);

  const handleAccountSelect = (account: GLMappingEntity) => {
    const accountId = String(account.account_id);
    setFromAccount(accountId);
    setFromAccountName(account.gl_name);
    onFromAccountChange(accountId);
  };

  const handleDateChange = (newDate: string) => {
    setDate(newDate);
    onDateChange(newDate);
  };

  const handleReferenceNumberChange = (refNum: string) => {
    setReferenceNumber(refNum);
    onReferenceNumberChange(refNum);
  };

  const handlePaymentModeChange = (mode: string) => {
    setPaymentMode(mode);
    onPaymentModeChange(mode);
  };

  return (
    <div className="flex w-full flex-col items-start gap-4">
      <div className="flex w-full flex-col items-start gap-2">
        <div className="flex w-full items-center gap-4">
          {isTally && (
            <div className="flex flex-col items-start gap-2 flex-1">
              <span className="text-body-bold font-body-bold text-default-font">
                From Account <span className="text-error-600">*</span>
              </span>
              <BankGlAccountDropdown
                preset="income"
                selectedAccountId={fromAccount}
                selectedAccountName={fromAccountName}
                onSelect={handleAccountSelect}
                placeholder="Select interest income account"
              />
            </div>
          )}
          <div className="flex flex-col items-start gap-2 flex-1">
            <span className="text-body-bold font-body-bold text-default-font">
              Date <span className="text-error-600">*</span>
            </span>
            <TextField className="w-full" label="" helpText="">
              <TextField.Input
                type="date"
                value={date}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleDateChange(event.target.value)
                }
              />
            </TextField>
          </div>
        </div>
        <div className="flex w-full items-center gap-4">
          <div className="flex flex-col items-start gap-2 flex-1">
            <span className="text-body-bold font-body-bold text-default-font">
              Received Via <span className="text-error-600">*</span>
            </span>
            <Select
              label=""
              helpText=""
              value={paymentMode}
              onValueChange={handlePaymentModeChange}
            >
              <Select.Item value="Bank Remittance">Bank Remittance</Select.Item>
              <Select.Item value="Bank Transfer">Bank Transfer</Select.Item>
              <Select.Item value="Cash">Cash</Select.Item>
              <Select.Item value="Cheque">Cheque</Select.Item>
              <Select.Item value="Credit Card">Credit Card</Select.Item>
              <Select.Item value="UPI">UPI</Select.Item>
            </Select>
          </div>
          <div className="flex flex-col items-start gap-2 flex-1">
            <span className="text-body-bold font-body-bold text-default-font">
              Reference Number
            </span>
            <TextField className="w-full" label="" helpText="">
              <TextField.Input
                placeholder="Enter reference number (optional)"
                value={referenceNumber}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleReferenceNumberChange(event.target.value)
                }
              />
            </TextField>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerInterestIncomeReco;
